/**
 * What survives of the Workers surfaces' data layer (YAG-371, cut by ADR-0044).
 *
 * House style, same as `projects/useProjects.ts`: plain `fetch` against a
 * relative `/api/...`, `credentials: "include"`, no shared client and no query
 * cache. The repo does not use react-query.
 *
 * What is gone, and why. `useEngagement` was the engagement pane's whole data
 * layer: the scope, load, authority, cadence and role-note legs of one PATCH.
 * Its `level` leg is refused by `routes/workers.ts` with a 409 now, because
 * Authority is per line and set on the Team page, and its `cadence` reads were
 * the Rhythm leg that a line's own clock trigger replaced. The review-queue,
 * PR-reviews, Harper-jobs and latest-work hooks went with the craft output
 * pages they fed. None of them had a second caller.
 *
 * What is left is what other surfaces read: the directory (the Team brief's
 * roster line), one pull request's reviews (the review case file), the
 * installation's repository names (the Team's Edit sheet), and Harper's run
 * shapes plus its retry (the Harper case file on Work). A Team's attachments
 * are read with the rest of the Team page (`teams/useTeamPage.ts`).
 */
import { useCallback, useEffect, useState } from "react";
import type {
  CodeReviewPrReviewsResponse,
  HarperGenerationReport,
  HarperUpdate,
  WorkerDirectoryResponse,
  WorkerDirectoryTeam,
  WorkerEngagementSummary,
  WorkerSummary,
  WrittenAs,
} from "@yagni/shared";

import { readRepoScope, type RepoScope } from "../desks/RepoScopeLine.js";

export type DirectoryWorker = WorkerSummary & {
  engagements: WorkerEngagementSummary[];
};

export interface UseWorkerDirectoryResult {
  workers: DirectoryWorker[];
  teams: WorkerDirectoryTeam[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useWorkerDirectory(): UseWorkerDirectoryResult {
  const [workers, setWorkers] = useState<DirectoryWorker[]>([]);
  const [teams, setTeams] = useState<WorkerDirectoryTeam[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetch("/api/workers", { credentials: "include" });
      if (!res.ok) throw new Error(`Workers load failed (${res.status})`);
      const body = (await res.json()) as Partial<WorkerDirectoryResponse>;
      setWorkers(Array.isArray(body.workers) ? (body.workers as DirectoryWorker[]) : []);
      setTeams(Array.isArray(body.teams) ? body.teams : []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Workers load failed"));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { workers, teams, isLoading, error, refetch };
}

/* ── what they did here (the Team's work, this worker's rows) ── */

/* ── the supervised review queue (YAG-591 §7) ── */

/* ── the PR Reviews subpage (YAG-392) ── */

export interface UsePrStateReviewsResult {
  data: CodeReviewPrReviewsResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  /**
   * "Re-run review": a state write the sweep picks up. A 409's body text is
   * the honest refusal the modal prints verbatim — never a pretend success.
   */
  rerun: () => Promise<{ ok: boolean; message: string }>;
}

/** ~10 s between silent polls while a review is running for the open modal. */
const REVIEW_POLL_MS = 10_000;

/** The desk table's own poll while a visible row is queued or reviewing. */
const DESK_PR_POLL_MS = 10_000;

/** Every recorded review for one watched PR, plus the derived header. */
export function usePrStateReviews(prStateId: string | null): UsePrStateReviewsResult {
  const [data, setData] = useState<CodeReviewPrReviewsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // Armed by a queued re-run: the rearm leaves NO in-progress report until
  // the next sweep claims the PR, so the poll must run on the arm alone.
  const [rerunArmed, setRerunArmed] = useState(false);

  const refetch = useCallback(async () => {
    if (!prStateId) {
      setData(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(
        `/api/code-review/pr-state/${encodeURIComponent(prStateId)}/reviews`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`Reviews load failed (${res.status})`);
      setData((await res.json()) as CodeReviewPrReviewsResponse);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Reviews load failed"));
    } finally {
      setIsLoading(false);
    }
  }, [prStateId]);

  useEffect(() => {
    setRerunArmed(false);
    void refetch();
  }, [refetch]);

  /**
   * The poll is silent: no isLoading flip (the modal must not flash every
   * tick) and a failed tick keeps the last good data — the next tick tries
   * again rather than swapping in the error view mid-read.
   */
  const silentRefetch = useCallback(async () => {
    if (!prStateId) return;
    try {
      const res = await fetch(
        `/api/code-review/pr-state/${encodeURIComponent(prStateId)}/reviews`,
        { credentials: "include" },
      );
      if (!res.ok) return;
      setData((await res.json()) as CodeReviewPrReviewsResponse);
      setError(null);
    } catch {
      // Kept: the last good data. A transient poll failure is not an error state.
    }
  }, [prStateId]);

  const hasInProgress = data?.reviews.some((review) => review.status === "in_progress") ?? false;

  // Once the sweep claims the re-run, the in-progress report carries the
  // polling and the arm has done its job — so a later terminal status stops it.
  useEffect(() => {
    if (rerunArmed && hasInProgress) setRerunArmed(false);
  }, [rerunArmed, hasInProgress]);

  useEffect(() => {
    if (!prStateId || (!hasInProgress && !rerunArmed)) return;
    const timer = setInterval(() => void silentRefetch(), REVIEW_POLL_MS);
    return () => clearInterval(timer);
  }, [prStateId, hasInProgress, rerunArmed, silentRefetch]);

  const rerun = useCallback(async (): Promise<{ ok: boolean; message: string }> => {
    if (!prStateId) return { ok: false, message: "" };
    try {
      const res = await fetch(`/api/code-review/pr-state/${encodeURIComponent(prStateId)}/rerun`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        return { ok: false, message: body?.error || `Could not re-run (${res.status})` };
      }
      setRerunArmed(true);
      await refetch();
      return { ok: true, message: "" };
    } catch {
      return { ok: false, message: "Could not re-run just now." };
    }
  }, [prStateId, refetch]);

  return { data, isLoading, error, refetch, rerun };
}

/* ── the review repositories surface (2026-09-04) ── */

/**
 * The GitHub App installation's repositories, as full owner/name strings,
 * for Works on in Team settings, and whose installation they come from
 * (`scope`, for the line under the search). Fires only when the sheet can
 * edit; quiet on every failure (GitHub not connected, token expired): no
 * suggestions is degraded, not broken.
 */
export function useAvailableRepos(enabled: boolean): { names: string[]; scope: RepoScope | null } {
  const [found, setFound] = useState<{ names: string[]; scope: RepoScope | null }>({
    names: [],
    scope: null,
  });
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/repos/available", { credentials: "include" });
        if (!res.ok) return;
        const data = (await res.json()) as {
          repos?: Array<{ fullName?: string }>;
        };
        const fullNames = (data.repos ?? [])
          .map((repo) => repo.fullName)
          .filter((name): name is string => typeof name === "string" && name.length > 0);
        if (!cancelled) setFound({ names: fullNames, scope: readRepoScope(data) });
      } catch {
        // Quiet: hand-typing still works without suggestions.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);
  return found;
}

/* ── Harper jobs: the jobs leg and the runs table (same house style) ── */

export interface HarperJobDelivery {
  kind: "slack";
  channelId: string;
}

export interface HarperJob {
  id: string;
  name: string;
  category: WrittenAs;
  active: boolean;
  instructions: string | null;
  notRelevant: string | null;
  sourceFilter: string | null;
  delivery: HarperJobDelivery | null;
  runCount?: number;
  lastRun?: { id: string; status: string; createdAt: string } | null;
}

export interface HarperRunRow {
  id: string;
  jobId: string;
  deskId: string;
  windowStart: string;
  windowEnd: string;
  status:
    | "in_progress"
    | "completed"
    | "nothing_to_file"
    | "delivery_failed"
    | "failed"
    | "held"
    | "needs_attention"
    | "superseded";
  /** `code` is a `@yagni/shared` job failure code; absent on a legacy row. */
  failure: { stage: string; reason: string; code?: string } | null;
  storyId: string | null;
  workItemId: string | null;
  createdAt: string;
  deliveredAt: string | null;
  jobName: string;
}

/** One stage of a run as the engine lists it, in its own order. */
export interface HarperRunStageEntry {
  id: string;
  label: string;
  /** `degraded`: the stage fell back to a simpler form (its `code` says how). */
  status: "done" | "degraded" | "failed" | "pending" | "running";
  completedAt: string | null;
  code?: string | null;
}

export interface HarperRunDetail {
  run: HarperRunRow & {
    stages: Record<string, { completedAt?: string; output?: unknown }>;
    findings: unknown[];
    /** Absent from an older backend; the case file then derives the stages itself. */
    stageList?: HarperRunStageEntry[];
    /** While it runs: the stage it is on. */
    currentStage?: { id: string; label: string; step: number; of: number } | null;
    /** The Update as it posted (or would post). */
    update?: HarperUpdate | null;
    /** How the Update was written. */
    report?: HarperGenerationReport | null;
  };
  job: HarperJob | null;
}

/** POST the retry. True when the retry started. */
export async function retryHarperRun(
  runId: string,
): Promise<{ ok: boolean; error: string | null }> {
  try {
    const res = await fetch(`/api/workers/harper/runs/${encodeURIComponent(runId)}/retry`, {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) {
      try {
        const body = (await res.json()) as { error?: string };
        return { ok: false, error: body.error ?? `Request failed (${res.status}).` };
      } catch {
        return { ok: false, error: `Request failed (${res.status}).` };
      }
    }
    return { ok: true, error: null };
  } catch {
    return { ok: false, error: "Network error. Please try again." };
  }
}
